import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
  useContext,
} from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  PersonAdd,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { ROLE_SYSTEM_ADMIN, ROLE_SALON_ADMIN } from "../../utils/constants";

import { baseAxios, Context } from "../../utils/config";
import { useAuth } from "../../contexts/auth";
import JsBarcode from "jsbarcode";
import notify from "devextreme/ui/notify";
import { PaginatedTable, CreateAccountForm } from "../../components";

const ROLES = [
  { id: "jdtech admin", access: ["QSolutions"] },
  { id: "system admin", access: ["QSolutions"] },
  { id: "salon admin", access: ["QSolutions", "QUE"] },
  { id: "front desk", access: ["QUE"] },
  { id: "stylist", access: ["QUE"] },
];

const STATUS_LABELS = {
  PENDING: "Pending",
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  DELETED: "Deleted",
};

const STATUS_CHIP_COLOR = {
  ACTIVE: "success",
  PENDING: "warning",
  INACTIVE: "default",
  DELETED: "error",
};

/** Icon + menu so column filters read as “filter”, not a second form control next to sort. */
function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        color={active ? "primary" : "default"}
        sx={{
          p: 0.35,
          ...(active
            ? {}
            : {
                color: "action.active",
                opacity: 0.72,
              }),
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HighlightPhone({ display, query }) {
  const str = String(display ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const queryDigits = q.replace(/\D/g, "");

  if (!queryDigits) return <HighlightText text={str} query={q} />;

  const digitIndexToCharIndex = [];
  for (let ci = 0; ci < str.length; ci++) {
    if (/\d/.test(str[ci])) digitIndexToCharIndex.push(ci);
  }

  const strDigits = digitIndexToCharIndex.map((ci) => str[ci]).join("");
  const matchStart = strDigits.indexOf(queryDigits);

  if (matchStart === -1) return <HighlightText text={str} query={q} />;

  const matchEnd = matchStart + queryDigits.length - 1;
  const charStart = digitIndexToCharIndex[matchStart];
  const charEnd = digitIndexToCharIndex[matchEnd];

  return [
    str.slice(0, charStart),
    <mark key="m" style={MARK_STYLE}>
      {str.slice(charStart, charEnd + 1)}
    </mark>,
    str.slice(charEnd + 1),
  ];
}

const normalizeDigits = (s) => String(s ?? "").replace(/\D/g, "");

/** Same idea as Edit-A-Client: when the query contains digits, normalize for phone/barcode matching. */
const normalizeSearchQuery = (value) => {
  const query = String(value ?? "").trim();
  if (!/\d/.test(query)) return query;
  return query.replace(/[\s(),-]/g, "");
};

const formatPhoneDisplay = (value) => {
  const digits = normalizeDigits(value);
  if (digits.length !== 10) return value ? `${value}` : "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatAccessLevel = (roles) =>
  (roles ?? [])
    .reduce(
      (acc, role) => [
        ...new Set([
          ...acc,
          ...(ROLES.find((r) => r.id === role)?.access ?? []),
        ]),
      ],
      []
    )
    .sort()
    .join(", ");

const normalizeUserRow = (user) => ({
  ...user,
  roles: Array.isArray(user.roles)
    ? user.roles.map((r) => (typeof r === "string" ? r : r.role))
    : [],
});

const salonRowKey = (row) =>
  `${row.org_id ?? "none"}:${row.location_id ?? "none"}`;

function salonRowLabel(row) {
  const org = String(row.org_name ?? "").trim();
  const salon = String(row.location_name ?? "").trim();
  if (org && salon) return `${salon} (${org})`;
  if (salon) return salon;
  if (org) return org;
  return "Unassigned";
}

function userDirectoryDisplayName(row) {
  const first = String(row.user_first_name ?? "").trim();
  const last = String(row.user_last_name ?? "").trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  return "";
}

function compareManageUserRows(a, b, field) {
  let primary = 0;

  if (field === "org_location") {
    const sa = [a.org_name, a.location_name]
      .filter((x) => x != null && String(x).trim() !== "")
      .join(" ");
    const sb = [b.org_name, b.location_name]
      .filter((x) => x != null && String(x).trim() !== "")
      .join(" ");
    primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  } else if (field === "display_name") {
    const sa = userDirectoryDisplayName(a);
    const sb = userDirectoryDisplayName(b);
    const emptyRank = (s) => (s === "" ? 1 : 0);
    const ra = emptyRank(sa);
    const rb = emptyRank(sb);
    if (ra !== rb) primary = ra - rb;
    else primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  } else {
    const va = a[field];
    const vb = b[field];
    const sa = va == null ? "" : Array.isArray(va) ? va.join(", ") : String(va);
    const sb = vb == null ? "" : Array.isArray(vb) ? vb.join(", ") : String(vb);
    primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  }

  if (primary !== 0) return primary;
  return (Number(a.user_id) || 0) - (Number(b.user_id) || 0);
}

/**
 * Match across org, salon, names (including partial first/last and order), barcode, phone, email, roles, status.
 */
function userMatchesKeywordSearch(user, rawQuery) {
  const q = String(rawQuery ?? "").trim();
  if (!q) return true;

  const normalizedDigitQuery = normalizeDigits(normalizeSearchQuery(q));
  const qLower = q.toLowerCase();
  const accessStr = formatAccessLevel(user.roles).toLowerCase();
  const first = String(user.user_first_name ?? "").toLowerCase();
  const last = String(user.user_last_name ?? "").toLowerCase();
  const roleNames = (user.roles ?? []).map((r) => String(r).toLowerCase());

  const textBlob = [
    user.org_name,
    user.location_name,
    user.user_first_name,
    user.user_last_name,
    user.user_email,
    user.user_status,
    STATUS_LABELS[user.user_status] ?? user.user_status,
    user.user_barcode,
    formatPhoneDisplay(user.user_phone),
    accessStr,
    ...roleNames,
    `${first} ${last}`.trim(),
    `${last} ${first}`.trim(),
    `${last}, ${first}`.trim(),
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const digitBlob =
    normalizeDigits(user.user_phone) + normalizeDigits(user.user_barcode);

  if (textBlob.includes(qLower)) return true;
  if (
    normalizedDigitQuery.length >= 2 &&
    digitBlob.includes(normalizedDigitQuery)
  )
    return true;

  const tokens = qLower.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => {
    const td = normalizeDigits(tok);
    if (td.length >= 2 && digitBlob.includes(td)) return true;
    return textBlob.includes(tok);
  });
}

export default function ManageUsers() {
  const { location: globalLocation } = useContext(Context);
  const { user } = useAuth();

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [dialogUserId, setDialogUserId] = useState(null);

  const openCreateUser = useCallback(() => {
    setDialogUserId(null);
    setAccountDialogOpen(true);
  }, []);

  const openEditUser = useCallback((userId) => {
    setDialogUserId(userId);
    setAccountDialogOpen(true);
  }, []);

  const closeAccountDialog = useCallback(() => {
    setAccountDialogOpen(false);
  }, []);

  const handleAccountDialogExited = useCallback(() => {
    setDialogUserId(null);
  }, []);

  const showOrganizationColumn = user.userRoles.some(
    (x) => x === ROLE_SYSTEM_ADMIN
  );

  const showLocationColumn = user.userRoles.some(
    (x) => x === ROLE_SYSTEM_ADMIN || x === ROLE_SALON_ADMIN
  );

  const [muiUsers, setMuiUsers] = useState([]);
  const [muiLoading, setMuiLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAccessLevel, setFilterAccessLevel] = useState("");
  const [filterSalonKey, setFilterSalonKey] = useState("");
  const [salonFilterMenuAnchor, setSalonFilterMenuAnchor] = useState(null);
  const [statusFilterMenuAnchor, setStatusFilterMenuAnchor] = useState(null);
  const [accessFilterMenuAnchor, setAccessFilterMenuAnchor] = useState(null);
  const [muiSortBy, setMuiSortBy] = useState("display_name");
  const [muiSortOrder, setMuiSortOrder] = useState("asc");

  const statusFilterOptions = useMemo(() => {
    const set = new Set();
    for (const row of muiUsers) {
      if (row.user_status) set.add(row.user_status);
    }
    return [...set].sort((a, b) =>
      String(STATUS_LABELS[a] ?? a).localeCompare(
        String(STATUS_LABELS[b] ?? b),
        undefined,
        { sensitivity: "base" }
      )
    );
  }, [muiUsers]);

  const accessLevelFilterOptions = useMemo(() => {
    const set = new Set();
    for (const row of muiUsers) {
      const s = formatAccessLevel(row.roles);
      if (s) set.add(s);
    }
    return [...set].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [muiUsers]);

  const salonFilterOptions = useMemo(() => {
    const map = new Map();
    for (const row of muiUsers) {
      const key = salonRowKey(row);
      if (!map.has(key)) map.set(key, salonRowLabel(row));
    }
    return [...map.entries()].sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { sensitivity: "base" })
    );
  }, [muiUsers]);

  const loadMuiUsers = useCallback(() => {
    setMuiLoading(true);
    baseAxios
      .get("/users")
      .then((res) => {
        const users = (res?.data?.users ?? []).map(normalizeUserRow);
        setMuiUsers(users);
      })
      .catch(() => {
        setMuiUsers([]);
        notify("Could not load users for directory.", "Error", 3000);
      })
      .finally(() => setMuiLoading(false));
  }, [globalLocation?.location_id]);

  const onAccountSaved = useCallback(() => {
    loadMuiUsers();
    closeAccountDialog();
  }, [loadMuiUsers, closeAccountDialog]);

  useEffect(() => {
    loadMuiUsers();
  }, [loadMuiUsers]);

  const filteredMuiUsers = useMemo(() => {
    return muiUsers.filter((row) => {
      if (!userMatchesKeywordSearch(row, deferredSearch)) return false;
      if (filterStatus && row.user_status !== filterStatus) return false;
      if (filterAccessLevel) {
        const acc = formatAccessLevel(row.roles);
        if (acc !== filterAccessLevel) return false;
      }
      if (filterSalonKey && salonRowKey(row) !== filterSalonKey) return false;
      return true;
    });
  }, [
    muiUsers,
    deferredSearch,
    filterStatus,
    filterAccessLevel,
    filterSalonKey,
  ]);

  const sortedMuiUsers = useMemo(() => {
    const copy = [...filteredMuiUsers];
    copy.sort((a, b) => {
      const c = compareManageUserRows(a, b, muiSortBy);
      return muiSortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredMuiUsers, muiSortBy, muiSortOrder]);

  const handleMuiSortChange = useCallback((field, order) => {
    setMuiSortBy(field);
    setMuiSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const muiColumns = useMemo(() => {
    const sq = deferredSearch;
    const cols = [];
    if (showOrganizationColumn || showLocationColumn) {
      cols.push({
        id: "org_location",
        label: "Salon",
        field: "org_location",
        minWidth: 200,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by salon"
            value={filterSalonKey}
            onChange={setFilterSalonKey}
            anchorEl={salonFilterMenuAnchor}
            setAnchorEl={setSalonFilterMenuAnchor}
            allMenuLabel="All salons"
            options={salonFilterOptions.map(([key, label]) => ({
              value: key,
              label,
            }))}
          />
        ),
        render: (_, row) => {
          const org = String(row.org_name ?? "").trim();
          const salon = String(row.location_name ?? "").trim();
          if (org && salon) {
            return (
              <Box sx={{ py: 0.25 }}>
                <Typography variant="body2" fontWeight={600} component="div">
                  <HighlightText text={salon} query={sq} />
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: "block", mt: 0.25, lineHeight: 1.3 }}
                >
                  <HighlightText text={org} query={sq} />
                </Typography>
              </Box>
            );
          }
          if (org) {
            return (
              <Typography variant="body2" fontWeight={600}>
                <HighlightText text={org} query={sq} />
              </Typography>
            );
          }
          if (salon) {
            return (
              <Typography variant="body2" fontWeight={600}>
                <HighlightText text={salon} query={sq} />
              </Typography>
            );
          }
          return "-";
        },
      });
    }
    cols.push(
      {
        id: "display_name",
        label: "Name",
        field: "display_name",
        render: (_, row) => {
          const name = userDirectoryDisplayName(row);
          return name ? <HighlightText text={name} query={sq} /> : "-";
        },
      },
      {
        id: "user_status",
        label: "Status",
        field: "user_status",
        minWidth: 132,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by status"
            value={filterStatus}
            onChange={setFilterStatus}
            anchorEl={statusFilterMenuAnchor}
            setAnchorEl={setStatusFilterMenuAnchor}
            allMenuLabel="All statuses"
            options={statusFilterOptions.map((s) => ({
              value: s,
              label: STATUS_LABELS[s] ?? s,
            }))}
          />
        ),
        render: (value) => {
          const label = STATUS_LABELS[value] ?? value;
          if (!label) return "-";
          return (
            <Chip
              size="small"
              variant="filled"
              label={label}
              color={STATUS_CHIP_COLOR[value] ?? "default"}
              sx={{ fontWeight: 500, lineHeight: 1 }}
            />
          );
        },
      },
      {
        id: "user_barcode",
        label: "Barcode",
        field: "user_barcode",
        sortable: false,
        render: (value) => (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <span>
              {value ? <HighlightText text={value} query={sq} /> : "-"}
            </span>
            {value ? (
              <IconButton
                size="small"
                aria-label="Download barcode"
                onClick={() => {
                  const canvas = document.createElement("canvas");
                  JsBarcode(canvas, value);
                  const link = document.createElement("a");
                  link.download = "barcode.png";
                  link.href = canvas.toDataURL();
                  link.click();
                }}
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ) : null}
          </Box>
        ),
      },
      {
        id: "roles",
        label: "Access Level",
        field: "roles",
        minWidth: 200,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by access level"
            value={filterAccessLevel}
            onChange={setFilterAccessLevel}
            anchorEl={accessFilterMenuAnchor}
            setAnchorEl={setAccessFilterMenuAnchor}
            allMenuLabel="All access levels"
            options={accessLevelFilterOptions.map((label) => ({
              value: label,
              label,
            }))}
          />
        ),
        render: (_, row) => {
          const access = formatAccessLevel(row.roles);
          return access ? <HighlightText text={access} query={sq} /> : "-";
        },
      },
      {
        id: "user_phone",
        label: "Phone Number",
        field: "user_phone",
        render: (value) => {
          if (!value) return "-";
          const digits = normalizeDigits(value);
          const display =
            digits.length === 10
              ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
              : String(value);
          return <HighlightPhone display={display} query={sq} />;
        },
      },
      {
        id: "user_email",
        label: "Email",
        field: "user_email",
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openEditUser(row.user_id)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              "& .MuiButton-startIcon": { mr: 0.5 },
            }}
          >
            Edit
          </MuiButton>
        ),
      }
    );
    return cols;
  }, [
    showOrganizationColumn,
    showLocationColumn,
    openEditUser,
    deferredSearch,
    filterStatus,
    filterAccessLevel,
    filterSalonKey,
    salonFilterMenuAnchor,
    statusFilterMenuAnchor,
    accessFilterMenuAnchor,
    statusFilterOptions,
    accessLevelFilterOptions,
    salonFilterOptions,
  ]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, search, and edit user accounts, roles, and access levels.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search org, salon, name, phone, barcode, email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh users"
            onClick={loadMuiUsers}
            disabled={muiLoading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton variant="contained" onClick={openCreateUser}>
            <PersonAdd sx={{ mr: 1 }} /> Add User
          </MuiButton>
        </Box>
      </Box>
      <PaginatedTable
        columns={muiColumns}
        rows={sortedMuiUsers}
        totalRowCount={sortedMuiUsers.length}
        loading={muiLoading}
        onPaginationChange={noopPagination}
        onSortChange={handleMuiSortChange}
        initialOrderBy="display_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterStatus}|${filterAccessLevel}|${filterSalonKey}`}
        clientSidePagination
        skeletonHeight="40px"
      />

      <Dialog
        open={accountDialogOpen}
        onClose={closeAccountDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        TransitionProps={{ onExited: handleAccountDialogExited }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              height: 44,
              px: 0.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.50",
            }}
          >
            <IconButton
              aria-label="Close"
              onClick={closeAccountDialog}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CreateAccountForm
              key={dialogUserId == null ? "new-user" : `user-${dialogUserId}`}
              userId={dialogUserId}
              onSaved={onAccountSaved}
              onUserUpdated={loadMuiUsers}
              onRequestClose={closeAccountDialog}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
